import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneTilt, ChatCircleDots, CheckCircle, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PANEL_INITIAL = { opacity: 0, height: 0 };
const PANEL_ANIMATE = { opacity: 1, height: "auto" };
const PANEL_EXIT = { opacity: 0, height: 0 };
const PANEL_TRANSITION = { duration: 0.2, ease: "easeOut" };

export default function ClientRequestForm({ token, taskId, taskTitle, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setName(""); setContact(""); setMessage("");
      setDone(false); setError("");
    }, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !message.trim()) {
      setError("Name and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/public/projects/${token}/tasks/${taskId}/requests`,
        { client_name: name.trim(), client_contact: contact.trim(), message: message.trim() }
      );
      setDone(true);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3">
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid={`request-open-${taskId}`}
          className="inline-flex items-center gap-2 label-overline text-blue-700 hover:text-blue-900 border-b border-transparent hover:border-blue-700 transition-colors"
        >
          <ChatCircleDots size={12} weight="bold" /> Request details on this phase
        </button>
      )}

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            initial={PANEL_INITIAL}
            animate={PANEL_ANIMATE}
            exit={PANEL_EXIT}
            transition={PANEL_TRANSITION}
            className="overflow-hidden"
          >
            <div
              className="mt-3 border-l-4 border-l-blue-600 border-y border-r border-zinc-300 bg-white p-4"
              data-testid={`request-form-${taskId}`}
            >
              {done ? (
                <div className="flex items-start gap-3 py-2">
                  <div className="w-8 h-8 bg-emerald-100 border border-emerald-600 flex items-center justify-center shrink-0">
                    <CheckCircle size={16} weight="bold" className="text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <div className="label-overline text-emerald-700">Request Sent</div>
                    <div className="text-sm text-zinc-700 mt-1">
                      Thanks — we&apos;ve received your note on <strong>{taskTitle}</strong> and will reply shortly.
                    </div>
                  </div>
                  <button
                    onClick={reset}
                    data-testid={`request-close-${taskId}`}
                    className="text-zinc-400 hover:text-zinc-900"
                    aria-label="Close"
                  >
                    <X size={16} weight="bold" />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="label-overline text-blue-700">
                      Request Details · <span className="text-zinc-900">{taskTitle}</span>
                    </div>
                    <button type="button" onClick={reset} aria-label="Close"
                            className="text-zinc-400 hover:text-zinc-900">
                      <X size={14} weight="bold" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="label-overline block mb-1.5 text-zinc-700">Your Name *</span>
                      <input
                        required maxLength={120}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid={`request-name-${taskId}`}
                        className="w-full rounded-none border border-zinc-400 px-3 py-2 text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                        placeholder="e.g. Rajesh Kumar"
                      />
                    </label>
                    <label className="block">
                      <span className="label-overline block mb-1.5 text-zinc-700">Email or Phone (optional)</span>
                      <input
                        maxLength={200}
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        data-testid={`request-contact-${taskId}`}
                        className="w-full rounded-none border border-zinc-400 px-3 py-2 text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                        placeholder="hello@example.com"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="label-overline block mb-1.5 text-zinc-700">What would you like to know? *</span>
                    <textarea
                      required maxLength={2000} rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      data-testid={`request-message-${taskId}`}
                      className="w-full rounded-none border border-zinc-400 px-3 py-2 text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 resize-y"
                      placeholder="e.g. Can we see the material options for the flooring? Also please share the parking layout drawings."
                    />
                    <div className="text-xs text-zinc-400 mt-1 font-mono">{message.length}/2000</div>
                  </label>

                  {error && (
                    <div className="border-l-4 border-red-600 bg-red-50 px-3 py-2 text-xs text-red-800"
                         data-testid={`request-error-${taskId}`}>
                      {error}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
                    <button
                      type="button" onClick={reset}
                      className="label-overline text-zinc-500 hover:text-zinc-900 px-3 py-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit" disabled={submitting}
                      data-testid={`request-submit-${taskId}`}
                      className="inline-flex items-center gap-2 label-overline bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 disabled:opacity-60"
                    >
                      <PaperPlaneTilt size={12} weight="bold" />
                      {submitting ? "Sending…" : "Send Request"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
